import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';
import lodashCompact from 'lodash/fp/compact';

import { isListingSunRise } from 'common/listingHelpers';

import {
  MARKETING_PARTNER_LISTING_STATUS_LANDLORD_INVITED,
  MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS,
  MARKETING_PARTNER_LISTING_STATUS_TEMPORARILY_OFF_MARKET,
  MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM,
} from 'common/marketingPartnerListingConstants';

import {
  LISTING_WALKTHROUGH_CONDITION_ITEMS,
  LISTING_WALKTHROUGH_CONDITION_OTHER,
} from 'common/listingConstants';

import {
  PARTNER_TYPE_AGENT,
} from 'common/partnerConstants';

import {
  DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
  DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_ALL,
  DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_TEMP_OFF_MARKET,
  DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_REFERRAL,
} from 'common/dataTableConstants';

import {
  SR_API_ACTION_PARTNER_MARK_MARKETING_LISTING_RESOLVED,
} from 'api/actionTypes';

import {
  dataTable,
  actionDataTableLoadNextPage,
  actionDataTableReset,
} from 'client/helpers/dataTables';

import withInfiniteScroll from 'client/helpers/withInfiniteScroll';
import branch from 'client/core/branch';
import postWithState from 'client/actions/postWithState';

import SrPartnerSection from 'client/components/SrPartnerSection';
import SrPartnerMarketListingModal from 'client/components/SrPartnerMarketListingModal';
import SrPartnerMarketListingEditDueDateModal from 'client/components/SrPartnerMarketListingEditDueDateModal';
import SrText from 'client/components/SrText';
import SrDarkModal from 'client/components/SrDarkModal';
import SrModalContent from 'client/components/SrModalContent';
import SrMarketingPartnerMarkAsResolvedModal from 'client/components/SrMarketingPartnerMarkAsResolvedModal';
import SrSimpleMessageModal from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrBox from 'client/components/SrBox';
import ButtonDeprecated, {
  BUTTON_THEME_YELLOW,
} from 'client/components/ButtonDeprecated';
import SrDataTableMarketingPartnerListingsCard from 'client/components/SrDataTableMarketingPartnerListingsCard';
import SrPartnerMarketListingEditFieldsModal from 'client/components/SrPartnerMarketListingEditFieldsModal';
import SrPartnerMarketingListingStatusUpdateModal from 'client/components/SrPartnerMarketingListingStatusUpdateModal';
import SrListingEditWalkThroughFieldsModal from 'client/components/SrListingEditWalkThroughFieldsModal';
import SrDataRetrievalText from 'client/components/SrDataRetrievalText/SrDataRetrievalText';
import SrPartnerContactSupportModal from 'client/components/SrPartnerContactSupportModal';
import SrGoingToMarketSunRiseListingCard from 'client/components/SrGoingToMarketSunRiseListingCard';
import SrUserSettingDropdownWithSearchBar from 'client/components/SrUserSettingDropdownWithSearchBar/SrUserSettingDropdownWithSearchBar';
import { USER_ROLE_ADMIN } from 'common/userRoles';

const getFilterOptions = (props) => {
  return lodashCompact([
    {
      label: 'All',
      value: DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_ALL,
    },
    {
      label: 'Temp. Off-Market',
      value: DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_TEMP_OFF_MARKET,
    },
    props.displayReferralProperties && {
      label: 'Owner Listings',
      value: DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_REFERRAL,
    },
  ]);
};

const SR_MARKETING_PARTNER_VIEW_ID_TO_FILTERS_MAPPING = {
  [DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_ALL]: {
    'marketingPartnerListings.status': [
      MARKETING_PARTNER_LISTING_STATUS_LANDLORD_INVITED,
      MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS,
      MARKETING_PARTNER_LISTING_STATUS_TEMPORARILY_OFF_MARKET,
    ],
  },
  [DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_TEMP_OFF_MARKET]: {
    'marketingPartnerListings.status': [
      MARKETING_PARTNER_LISTING_STATUS_TEMPORARILY_OFF_MARKET,
    ],
  },
  [DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_REFERRAL]: {
    'marketingPartnerListings.status': [
      MARKETING_PARTNER_LISTING_STATUS_LANDLORD_INVITED,
      MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS,
      MARKETING_PARTNER_LISTING_STATUS_TEMPORARILY_OFF_MARKET,
    ],
    'marketingPartnerListings.listingAgreementSignedBy': MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM, // eslint-disable-line max-len
  },
};

export function getDataTableSettingsFromProps(props) {
  const rootViewId = props.marketingListingsViewId || DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_ALL;
  const filters = SR_MARKETING_PARTNER_VIEW_ID_TO_FILTERS_MAPPING[rootViewId];

  const viewId = lodashCompact([
    rootViewId,
    props.partner ? `partner-id-${props.partner.id}` : '',
    props.listingId ? `listing-id-${props.listingId}` : '',
  ]).join('_');

  const marketIds = props.currentUser.settings.partnerPortal?.goingToMarket?.[props.partner?.slug + '_marketIds']; // eslint-disable-line max-len

  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
    viewId: viewId,
    filter: {
      ...filters,
    },
    sort: {
      'marketingPartnerListings.dueDate': props.sortOrder || 'asc',
    },
    count: 30,
    itemsPropName: props.itemsPropName,
  };

  if (props.partner) {
    dataTableSettings.filter['marketingPartnerListings.partnerId'] = props.partner.id;
  }

  if (props.listingId) {
    dataTableSettings.filter['marketingPartnerListings.listingId'] = props.listingId;
  }

  if (props.partner && marketIds && marketIds.length > 0) {
    dataTableSettings.customParams = {
      marketingPartnerListings: {
        marketIds: marketIds,
      },
    };
    dataTableSettings.rootViewId = `${viewId}_marketIds:${marketIds.join('_')}`; // eslint-disable-line max-len
    dataTableSettings.viewId = `${viewId}_marketIds:${marketIds.join('_')}`; // eslint-disable-line max-len
  }

  return dataTableSettings;
}

@withRouter
@branch({
  currentUser: ['currentUser'],
  marketingListingsViewId: ['partners', 'marketingListingsViewId'],
  screenId: ['analytics', 'screenId'],
  appUrl: ['env', 'APP_URL'],
  totalListings: ['dataTables', DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS],
})
@dataTable(getDataTableSettingsFromProps)
@withInfiniteScroll({
  offset: 32,
})
class SrDataTableMarketingPartnerListings extends Component {
  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    partner: PropTypes.object,
    items: PropTypes.array.isRequired,
    marketingListingsViewId: PropTypes.string,
    dispatch: PropTypes.func.isRequired,
    dataTableApiState: PropTypes.object.isRequired,
    dataTableIsFullyLoaded: PropTypes.bool.isRequired,
    onInfiniteScrollWrapperRef: PropTypes.func.isRequired,
    onInfiniteScrollCallbackReady: PropTypes.func.isRequired,
    reloadDataTableMarketingPartnerListing: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
    sortOrder: PropTypes.string,
    screenId: PropTypes.string.isRequired,
    location: PropTypes.object,
    appUrl: PropTypes.string.isRequired,
    displayReferralProperties: PropTypes.bool,
    accountManagerId: PropTypes.string,
    serviceType: PropTypes.string,
    setupHealthFilter: PropTypes.bool,
  }

  constructor(props) {
    super(props);

    this.state = {
      marketingPropertyListingModalProps: null,
      marketingPropertyListingAgentModalProps: null,
      modalPartnerWalkthroughListingId: null,
      marketingPropertyListingEditDueDateModalProps: null,
      marketingPropertyListingEditFieldsModalProps: null,
      marketingPropertyListingWithdrawalSupportModalProps: null,
      markAsResolvedModalIsOpen: false,
      resolvedConfirmationModalIsOpen: false,
    };

    const { onInfiniteScrollCallbackReady } = props;
    onInfiniteScrollCallbackReady(this._infiniteScrollCallback);
  }

  UNSAFE_componentWillMount() {
    const { partnerListingsViewId } = this.props;
    if (!partnerListingsViewId) {
      this._onFilterClick(DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_ALL);
    }
  }

  _infiniteScrollCallback = () => {
    const { dataTableIsFullyLoaded, dispatch } = this.props;
    if (!dataTableIsFullyLoaded) {
      dispatch(actionDataTableLoadNextPage, getDataTableSettingsFromProps(this.props));
    }
  }

  _onEditMarketingPartnerListing = (marketingPartnerListing) => {
    if (marketingPartnerListing.partner.type === PARTNER_TYPE_AGENT) {
      this.setState({
        marketingPropertyListingAgentModalProps: {
          marketingPartnerListing,
          partner: marketingPartnerListing.partner,
        },
      });
    }
    else {
      this.setState({
        marketingPropertyListingModalProps: {
          marketingPartnerListing,
          partner: marketingPartnerListing.partner,
        },
      });
    }
  }

  _onEditMarketingPartnerListingDueDate = (marketingPartnerListing) => {
    this.setState({
      marketingPropertyListingEditDueDateModalProps: {
        marketingPartnerListing,
      },
    });
  }

  _onEditMarketingPartnerListingFields = (marketingPartnerListing, fields) => {
    this.setState({
      marketingPropertyListingEditFieldsModalProps: {
        marketingPartnerListing,
        ...fields,
      },
    });
  }

  _onEditWalkThroughFields = (marketingPartnerListing) => {
    this.setState({
      listingEditWalkThroughFieldsModalProps: {
        marketingPartnerListing,
      },
    });
  }

  _onClosePartnerMarketListingModal = () => {
    this.setState({
      marketingPropertyListingModalProps: null,
    });
  }

  _onCloseAgentPartnerMarketListingModal = () => {
    this.setState({
      marketingPropertyListingAgentModalProps: null,
    });
  }

  _onClosePartnerMarketListingEditDueDateModal = () => {
    this.setState({
      marketingPropertyListingEditDueDateModalProps: null,
    });
  }

  _onClosePartnerMarketListingEditFieldsModal = () => {
    this.setState({
      marketingPropertyListingEditFieldsModalProps: null,
    });
  }

  _onCloseListingEditWalkThroughFieldsModal = () => {
    this.setState({
      listingEditWalkThroughFieldsModalProps: null,
    });
  }

  _onFilterClick = (viewId) => {
    const { dispatch } = this.props;
    dispatch(({ state }) => {
      state.set(['partners', 'marketingListingsViewId'], viewId);
      state.commit();
    });
  }

  _onOpenPartnerListingModalClick = (marketingPartnerListing) => {
    this.setState({
      modalPartnerWalkthroughListingId: marketingPartnerListing.id,
    });
  }

  _onClosePartnerListingModalClick = () => {
    this.setState({
      modalPartnerWalkthroughListingId: null,
    });
  }

  _onOpenWithdrawalSupportModal = (marketingPartnerListing) => {
    this.setState({
      marketingPropertyListingWithdrawalSupportModalProps: {
        marketingPartnerListing: marketingPartnerListing,
      },
      marketingPropertyListingAgentModalProps: null,
    });
  }

  _onCloseWithdrawalSupportModal = () => {
    this.setState({
      marketingPropertyListingWithdrawalSupportModalProps: null,
    });
  }

  _onMarkAsResolvedButtonClick = () => {
    this.setState({
      markAsResolvedModalIsOpen: true,
    });
  }

  _onMarkAsResolvedModalSubmit = (formValues) => {
    const { dispatch } = this.props;
    const { modalPartnerWalkthroughListingId } = this.state;

    this.setState({
      resolvedConfirmationModalIsOpen: true,
      markAsResolvedModalIsOpen: false,
    });

    if (modalPartnerWalkthroughListingId) {
      dispatch(postWithState, {
        action: SR_API_ACTION_PARTNER_MARK_MARKETING_LISTING_RESOLVED,
        payload: {
          marketingPartnerListingId: modalPartnerWalkthroughListingId,
          notes: formValues.notes,
        },
      }).then(() => {
        return dispatch(actionDataTableReset, {
          dataSource: DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
        });
      });
    }
  }

  _closeMarkAsResolvedModal = () => {
    this.setState({
      markAsResolvedModalIsOpen: false,
    });
  }

  _closeResolvedConfirmationModal = () => {
    this.setState({
      resolvedConfirmationModalIsOpen: false,
      modalPartnerWalkthroughListingId: null,
    });
  }

  _applyAccountManagerFilter = (marketingPartnerListings, accountManagerId) => {
    return marketingPartnerListings.filter((marketingPartnerListing) => {
      return Number(accountManagerId) === marketingPartnerListing.accountManagerUserId;
    });
  }

  _applyServiceTypeFilter = (marketingPartnerListings, serviceType) => {
    return marketingPartnerListings.filter((marketingPartnerListing) => {
      return marketingPartnerListing.services.includes(serviceType);
    });
  }

  _applySetupHealthFilter = (marketingPartnerListings, setupHealthFilter) => {
    if (!setupHealthFilter) {
      return marketingPartnerListings;
    }
    return marketingPartnerListings.filter((marketingPartnerListing) => {
      return marketingPartnerListing.setupHealth?.isHealthy === false;
    });
  }

  _applyFilters = (marketingPartnerListings) => {
    const {
      accountManagerId,
      serviceType,
      setupHealthFilter,
    } = this.props;

    let filteredListings = marketingPartnerListings;

    if (accountManagerId) {
      filteredListings = this._applyAccountManagerFilter(filteredListings, accountManagerId);
    }
    if (serviceType) {
      filteredListings = this._applyServiceTypeFilter(filteredListings, serviceType);
    }
    if (setupHealthFilter) {
      filteredListings = this._applySetupHealthFilter(filteredListings, setupHealthFilter);
    }

    return filteredListings;
  }

  render() {
    const {
      items: marketingPartnerListings,
      marketingListingsViewId,
      onInfiniteScrollWrapperRef,
      dataTableApiState,
      dataTableIsFullyLoaded,
      currentUser,
      dispatch,
      reloadDataTableMarketingPartnerListing,
      location,
      appUrl,
      totalListings,
      partner,
    } = this.props;

    const {
      markAsResolvedModalIsOpen,
      resolvedConfirmationModalIsOpen,
      marketingPropertyListingWithdrawalSupportModalProps,
      marketingPropertyListingModalProps,
      marketingPropertyListingAgentModalProps,
      marketingPropertyListingEditDueDateModalProps,
      marketingPropertyListingEditFieldsModalProps,
      listingEditWalkThroughFieldsModalProps,
      modalPartnerWalkthroughListingId,
    } = this.state;

    const modalPartnerListing = marketingPartnerListings
      .find(({ id }) => id === modalPartnerWalkthroughListingId);

    if (totalListings?.items?.length === 0 && dataTableIsFullyLoaded) {
      return null;
    }

    const filteredMarketingPartnerListings = this._applyFilters(marketingPartnerListings);

    const isAdmin = currentUser.roles.includes(USER_ROLE_ADMIN);

    return (
      <div className='SrDataTableMarketingPartnerListings'>
        {markAsResolvedModalIsOpen && (
          <SrMarketingPartnerMarkAsResolvedModal
            onSubmit={this._onMarkAsResolvedModalSubmit}
            onClose={this._closeMarkAsResolvedModal}
          />
        )}
        {resolvedConfirmationModalIsOpen && (
          <SrSimpleMessageModal
            iconUrl='/images/checkmarkSuccessGreen.svg'
            headline='Resolved!'
            message={
              'Awesome! Our Field Ops just got a notification to go back to the property to ' +
              `complete the setup. ${modalPartnerListing
                ? modalPartnerListing.listing.addressStreetDisplay
                : ''
              } will be active soon!`
            }
            buttonText='done'
            maxWidth={375}
            onClose={this._closeResolvedConfirmationModal}
            onButtonClick={this._closeResolvedConfirmationModal}
          />
        )}
        {modalPartnerListing && !resolvedConfirmationModalIsOpen && (
          <SrDarkModal
            className='SrDataTableMarketingPartnerListingsModal'
            onClose={this._onClosePartnerListingModalClick}
          >
            <SrModalContent width={690} dynamicHeight>
              <div className='container'>
                <div className='header'>
                  <span
                    size='2xl'
                    weight='bold'
                    leading='35'
                    className='title'
                  >
                    {modalPartnerListing.listing.addressStreetDisplay}
                  </span>
                  <span
                    size='2xl'
                    weight='light'
                    leading='35'
                    className='subtitle'
                  >
                    Walkthrough notes from Sunroom Field Ops
                  </span>
                </div>
                <div className='content'>
                  <SrBox paddingTop='5'>
                    <SrText
                      font='whitney-sc'
                      size='large'
                      weight='bold'
                    >
                      reason it’s not ready to list
                    </SrText>
                    {LISTING_WALKTHROUGH_CONDITION_ITEMS
                      .filter(({ value }) => (
                        (modalPartnerListing.walkthroughConditions || []).includes(value)
                        && value !== LISTING_WALKTHROUGH_CONDITION_OTHER
                      ))
                      .concat([{ label: modalPartnerListing.walkthroughExtraCondition || '' }])
                      .concat([{ label: modalPartnerListing.walkthroughNotes || '' }])
                      .filter(({ label }) => label)
                      .map(({ label }) => (
                        <div key={label} className='walkthroughNoteRow'>
                          <SrText
                            size='2xl'
                            weight='light'
                            leading='35'
                          >
                            {`“${label.replace('No: ', '')}”`}
                          </SrText>
                        </div>
                      ))
                    }
                  </SrBox>
                  <SrBox paddingTop='5'>
                    {(modalPartnerListing.walkthroughPhotoUrls || []).length > 0 && (
                      <SrText
                        font='whitney-sc'
                        size='large'
                        weight='bold'
                      >
                        photo proof
                      </SrText>
                    )}
                    {(modalPartnerListing.walkthroughPhotoUrls || []).map((url) => (
                      <div key={url}>
                        <img src={url} alt={url} style={{ width: '100%' }} />
                      </div>
                    ))}
                  </SrBox>
                </div>
                <div className='footer'>
                  <ButtonDeprecated
                    theme={BUTTON_THEME_YELLOW}
                    onClick={this._onMarkAsResolvedButtonClick}
                    type='button'
                  >
                    mark as resolved
                  </ButtonDeprecated>
                </div>
              </div>
            </SrModalContent>
          </SrDarkModal>
        )}
        {/* Reference to force scroll when an agent finishes adding a listing to market */}
        <div id='partnerOverviewGoingToMarketSection' style={{ scrollMargin: '120px' }} />
        <SrPartnerSection
          title='Going to Market'
          headerRow={
            <div className='partnerDataTableFilterContainer flex' style={{ lineHeight: '40px' }}>
              {partner && partner.markets?.length > 1 && (
                <SrUserSettingDropdownWithSearchBar
                  title='Market'
                  notFoundTitle='No markets found'
                  settingField={`partnerPortal.goingToMarket.${partner.slug}_marketIds`}
                  dropdownOptions={partner.markets.map((option) => ({
                    label: option.displayNameFull,
                    value: option.marketId,
                  }))}
                />
              )}
              {getFilterOptions(this.props).map(({ label, value }) => {
                const isActive = value === marketingListingsViewId;
                return (
                  <div key={value} style={{ margin: '0 0 0 25px' }}>
                    <SrText
                      size='large'
                      weight='medium'
                      color='blueGreycliff'
                      cursor={isActive ? null : 'pointer'}
                      decoration={isActive ? 'underline-yellow' : null}
                      font='greycliff'
                      opacity={isActive ? null : 0.5}
                      onClick={isActive ? null : () => this._onFilterClick(value)}
                      className='partnerDataTableFilterOption'
                    >
                      {label}
                    </SrText>
                  </div>
                );
              })}
            </div>
          }
        >
          {filteredMarketingPartnerListings && (
            <div className='listings' ref={onInfiniteScrollWrapperRef}>
              {filteredMarketingPartnerListings.map((item) => {
                const isSunRiseListing = isListingSunRise(item.listing);

                if (!isSunRiseListing) {
                  return (
                    <SrDataTableMarketingPartnerListingsCard
                      key={item.id}
                      partner={item.partner}
                      marketingPartnerListing={item}
                      currentUser={currentUser}
                      onEdit={() => this._onEditMarketingPartnerListing(item)}
                      onOpenPartnerListingModal={() => this._onOpenPartnerListingModalClick(item)}
                      onEditWalkThroughFields={() => this._onEditWalkThroughFields(item)}
                      onEditMarketingPartnerListingDueDate={() => this._onEditMarketingPartnerListingDueDate(item)} // eslint-disable-line max-len
                      onEditFields={(fields) => this._onEditMarketingPartnerListingFields(item, fields)}
                      location={location}
                      appUrl={appUrl}
                      reloadData={reloadDataTableMarketingPartnerListing}
                    />
                  );
                }

                return (
                  <SrGoingToMarketSunRiseListingCard
                    key={item.id}
                    isAdmin={isAdmin}
                    marketingListing={item}
                    onEditWalkThroughFields={() => this._onEditWalkThroughFields(item)}
                    onEditMarketingPartnerListingDueDate={() => this._onEditMarketingPartnerListingDueDate(item)} // eslint-disable-line max-len
                    reloadData={reloadDataTableMarketingPartnerListing}
                  />
                );
              })}
            </div>
          )}

          {!filteredMarketingPartnerListings.length && dataTableIsFullyLoaded && (
            <SrDataRetrievalText
              text={marketingListingsViewId === DATA_TABLE_VIEW_ID_MARKETING_PARTNER_LISTINGS_ALL
                ? 'There are no listings in the selected market.'
                : 'There is nothing to show for selected filters'
              }
            />
          )}

          {dataTableApiState.inProgress && (
            <div className='loading-ellipses' />
          )}
        </SrPartnerSection>

        {marketingPropertyListingModalProps && (
          <SrPartnerMarketListingModal
            {...marketingPropertyListingModalProps}
            isNew={false}
            reloadDataTableMarketingPartnerListing={reloadDataTableMarketingPartnerListing}
            onClose={this._onClosePartnerMarketListingModal}
            currentUser={currentUser}
          />
        )}
        {marketingPropertyListingAgentModalProps && (
          <SrPartnerMarketingListingStatusUpdateModal
            {...marketingPropertyListingAgentModalProps}
            onClose={this._onCloseAgentPartnerMarketListingModal}
            onWithdrawClick={() => this._onOpenWithdrawalSupportModal(
              marketingPropertyListingAgentModalProps.marketingPartnerListing
            )}
            appUrl={appUrl}
          />
        )}
        {marketingPropertyListingEditDueDateModalProps && (
          <SrPartnerMarketListingEditDueDateModal
            {...marketingPropertyListingEditDueDateModalProps}
            dispatch={dispatch}
            reloadDataTableMarketingPartnerListing={reloadDataTableMarketingPartnerListing}
            onClose={this._onClosePartnerMarketListingEditDueDateModal}
          />
        )}
        {marketingPropertyListingEditFieldsModalProps && (
          <SrPartnerMarketListingEditFieldsModal
            {...marketingPropertyListingEditFieldsModalProps}
            dispatch={dispatch}
            reloadDataTableMarketingPartnerListing={reloadDataTableMarketingPartnerListing}
            onClose={this._onClosePartnerMarketListingEditFieldsModal}
          />
        )}
        {listingEditWalkThroughFieldsModalProps && (
          <SrListingEditWalkThroughFieldsModal
            {...listingEditWalkThroughFieldsModalProps}
            dispatch={dispatch}
            reloadDataTableMarketingPartnerListing={reloadDataTableMarketingPartnerListing}
            onClose={this._onCloseListingEditWalkThroughFieldsModal}
          />
        )}
        {marketingPropertyListingWithdrawalSupportModalProps && (
          <SrPartnerContactSupportModal
            {...marketingPropertyListingWithdrawalSupportModalProps}
            onClose={this._onCloseWithdrawalSupportModal}
            headline='To withdraw this listing, contact your dedicated Account Manager'
          />
        )}
      </div>
    );
  }
}

SrDataTableMarketingPartnerListings.displayName = 'SrDataTableMarketingPartnerListings';

export default SrDataTableMarketingPartnerListings;
